import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { logInfo, logError } from '../config_and_helpers/logger';
import { showToast } from '../config_and_helpers/helpers';
import { isCanceledRequest } from '../utils/listSearchRequest';

/**
 * Fetch supplier credits for a given supplier (for dropdown when creating/editing an order).
 *
 * @param {string} token - The access token for the current user.
 * @param {number} supplierId - The supplier ID to fetch credits for.
 * @returns {Promise<Array>} Array of credit objects with id, supplier, order, initial_amount, currency, name, remaining_amount. Returns [] on error or empty response.
 */
export const getSupplierCredits = async (token, supplierId) => {
  if (!supplierId) return [];
  try {
    const response = await axios.get(
      `${BACKEND_URL}supplier-credits/?supplier_id=${supplierId}`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    logError(
      'supplier_credit_client',
      'getSupplierCredits failed',
      error.response?.data ?? error
    );
    return [];
  }
};

/**
 * Fetch a paginated list of supplier credits with optional search and supplier filtering.
 *
 * @param {string} token - The access token for the current user.
 * @param {Function} setCredits - State setter for the credits array.
 * @param {Object} options
 * @param {string} options.searchInput - Text search term.
 * @param {string} options.supplierId - Supplier ID to filter by.
 * @param {string|null} options.nextPage - URL for the next page of results.
 * @returns {Promise<Object>} { success, nextPage?, reachedEnd } or array of error messages.
 */
export const getSupplierCreditsList = async (
  token,
  setCredits,
  options = {}
) => {
  const {
    searchInput = '',
    supplierId = '',
    nextPage = null,
    sortBy = '',
    sortDirection = '',
    signal = undefined,
  } = options;

  let url = nextPage
    ? nextPage
    : `${BACKEND_URL}supplier-credits/?page_num=1`;

  // Only append filters/sort for first-page requests.
  // For pagination requests, backend-provided nextPage is authoritative.
  if (!nextPage) {
    if (searchInput) {
      url += `&search=${searchInput}`;
    }
    if (supplierId) {
      url += `&supplier_id=${supplierId}`;
    }
    if (sortBy) {
      url += `&sort_by=${sortBy}`;
    }
    if (sortDirection) {
      url += `&sort_direction=${sortDirection}`;
    }
  }

  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Token ${token}` },
      signal,
    });

    const data = response.data;
    const isPaginated = data && !Array.isArray(data) && 'results' in data;
    const results = isPaginated ? data.results : (Array.isArray(data) ? data : []);
    const nextCursor = isPaginated ? data.next : null;

    if (!nextCursor) {
      if (nextPage) {
        setCredits(prev => [...prev, ...results]);
      } else {
        setCredits(results);
      }
      return { success: true, reachedEnd: true };
    }

    if (!nextPage) {
      setCredits(results);
    } else {
      setCredits(prev => [...prev, ...results]);
    }

    return { success: true, nextPage: nextCursor };
  } catch (error) {
    if (isCanceledRequest(error)) {
      return { canceled: true };
    }
    logError(
      'supplier_credit_client',
      'getSupplierCreditsList failed',
      error.response?.data ?? error
    );
    return Object.values(error.response?.data ?? { error: error.message });
  }
};

/**
 * Create a new supplier credit.
 *
 * @param {string} token - The access token for the current user.
 * @param {Object} data - Credit data: supplier (id), order (id of related order), initial_amount, currency, name (optional).
 * @returns {Promise<Object|Array>} On success: { success: true, data }. On failure: array of error messages for toast.
 */
export const createSupplierCredit = async (token, data) => {
  try {
    const response = await axios.post(
      `${BACKEND_URL}supplier-credits/`,
      {
        supplier: data.supplier,
        ...(data.order != null ? { order: data.order } : {}),
        initial_amount: data.initial_amount,
        ...(data.remaining_amount != null ? { remaining_amount: data.remaining_amount } : {}),
        currency: data.currency,
        ...(data.name != null && data.name !== '' ? { name: data.name } : {}),
      },
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    logInfo('supplier_credit_client', 'createSupplierCredit success', { id: response.data?.id });
    return { success: true, data: response.data };
  } catch (error) {
    logError(
      'supplier_credit_client',
      'createSupplierCredit failed',
      error.response?.data ?? error
    );
    const payload = error.response?.data;
    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === 'object')
      return Object.values(payload).flat().filter(Boolean);
    return [error.message || 'Failed to create credit'];
  }
};

/**
 * Update an existing supplier credit (partial update).
 *
 * @param {string} token - The access token for the current user.
 * @param {number} creditId - The supplier credit ID.
 * @param {Object} data - Fields to update: initial_amount, remaining_amount, currency, name, order.
 * @returns {Promise<Object|Array>} On success: { success: true, data }. On failure: array of error messages for toast.
 */
export const updateSupplierCredit = async (token, creditId, data) => {
  try {
    const response = await axios.patch(
      `${BACKEND_URL}supplier-credits/${creditId}/`,
      {
        ...(data.initial_amount != null ? { initial_amount: data.initial_amount } : {}),
        ...(data.remaining_amount != null ? { remaining_amount: data.remaining_amount } : {}),
        ...(data.currency ? { currency: data.currency } : {}),
        ...(data.name !== undefined ? { name: data.name || null } : {}),
        ...(data.order !== undefined ? { order: data.order || null } : {}),
      },
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    logInfo('supplier_credit_client', 'updateSupplierCredit success', { id: creditId });
    return { success: true, data: response.data };
  } catch (error) {
    logError(
      'supplier_credit_client',
      'updateSupplierCredit failed',
      error.response?.data ?? error
    );
    const payload = error.response?.data;
    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === 'object')
      return Object.values(payload).flat().filter(Boolean);
    return [error.message || 'Failed to update credit'];
  }
};

/**
 * Delete a supplier credit.
 *
 * @param {string} token - The access token for the current user.
 * @param {number} creditId - The supplier credit ID.
 * @returns {Promise<Object|Array>} On success: { success: true, toast }. On failure: array of error messages.
 */
export const deleteSupplierCredit = async (token, creditId) => {
  try {
    await axios.delete(`${BACKEND_URL}supplier-credits/${creditId}/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });
    logInfo('supplier_credit_client', 'deleteSupplierCredit success', { id: creditId });
    return {
      success: true,
      toast: () =>
        showToast(
          'Supplier credit deleted successfully!',
          'success',
          'top-right',
          3000
        ),
    };
  } catch (error) {
    logError(
      'supplier_credit_client',
      'deleteSupplierCredit failed',
      error.response?.data ?? error
    );
    const payload = error.response?.data;
    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === 'object')
      return Object.values(payload).flat().filter(Boolean);
    return [error.message || 'Failed to delete credit'];
  }
};

/**
 * Fetch detailed information for a single supplier credit, including
 * its originating quote and orders that used this credit.
 *
 * @param {string} token - The access token for the current user.
 * @param {number} creditId - The supplier credit ID.
 * @returns {Promise<Object>} The detailed supplier credit data.
 * @throws Will throw an error if the request fails.
 */
export const getSupplierCreditDetails = async (token, creditId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}supplier-credits/${creditId}/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    logError(
      'supplier_credit_client',
      'getSupplierCreditDetails failed',
      error.response?.data ?? error
    );
    throw error;
  }
};
